import React, { createContext, useContext, useState, useEffect } from 'react';

type Currency = {
  code: string;
  symbol: string;
  rate: number;
};

type CurrencyContextType = {
  currentCurrency: Currency;
  currencies: Currency[];
  setCurrency: (code: string) => void;
  formatPrice: (price: number | string) => string;
  detectUserLocation: () => Promise<void>;
};

const currencies: Currency[] = [
  { code: 'INR', symbol: '₹', rate: 1 },
  { code: 'USD', symbol: '$', rate: 0.012 },
  { code: 'CAD', symbol: 'C$', rate: 0.016 }
];

const CurrencyContext = createContext<CurrencyContextType | null>(null);

export const CurrencyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentCurrency, setCurrentCurrency] = useState<Currency>(currencies[0]);
  const [locationDetected, setLocationDetected] = useState(false);

  const detectUserLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      
      // Map country codes to currency codes
      const countryCurrencyMap: { [key: string]: string } = {
        'IN': 'INR',
        'CA': 'CAD',
        'US': 'USD'
      };

      const detectedCurrencyCode = countryCurrencyMap[data.country_code];
      if (detectedCurrencyCode) {
        const currency = currencies.find(c => c.code === detectedCurrencyCode);
        if (currency && !locationDetected) {
          setCurrentCurrency(currency);
          localStorage.setItem('currency', detectedCurrencyCode);
          setLocationDetected(true);
        }
      }
    } catch (error) {
      console.error('Error detecting location:', error);
    }
  };

  useEffect(() => {
    const savedCurrency = localStorage.getItem('currency');
    if (savedCurrency) {
      const currency = currencies.find(c => c.code === savedCurrency);
      if (currency) {
        setCurrentCurrency(currency);
        setLocationDetected(true);
      }
    } else {
      detectUserLocation();
    }
  }, []);

  const setCurrency = (code: string) => {
    const currency = currencies.find(c => c.code === code);
    if (currency) {
      setCurrentCurrency(currency);
      localStorage.setItem('currency', code);
    }
  };

  const formatPrice = (price: number | string): string => {
    const numericPrice = typeof price === 'string' 
      ? parseFloat(price.replace(/[^0-9.]/g, ''))
      : price;
    
    const convertedPrice = numericPrice * currentCurrency.rate;
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currentCurrency.code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedPrice);
  };

  return (
    <CurrencyContext.Provider value={{
      currentCurrency,
      currencies,
      setCurrency,
      formatPrice,
      detectUserLocation,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};